import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, Trash2, Download, Share2, Globe, Lock, LinkIcon, Mic, Users } from "lucide-react";
import api, { API, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TABS = [["all", "All"], ["draft", "Drafts"], ["completed", "Completed"], ["published", "Published"], ["archived", "Archived"]];
const PRIV_ICON = { public: Globe, unlisted: LinkIcon, private: Lock };

export default function MyRecordings() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);

  const load = () => api.get("/recordings/mine").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  const filtered = tab === "all" ? items : items.filter((r) => r.status === tab || (tab === "published" && r.privacy === "public"));

  const del = async (id) => {
    if (!window.confirm("Delete this recording permanently? This won't restore your free recording count.")) return;
    try { await api.delete(`/recordings/${id}`); toast.success("Recording deleted."); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const setPrivacy = async (id, privacy) => {
    try { await api.patch(`/recordings/${id}`, { privacy }); toast.success("Privacy updated."); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleDuet = async (r) => {
    try { await api.patch(`/recordings/${r.recording_id}`, { duet_open: !r.duet_open }); toast.success(!r.duet_open ? "Open for duets — share the link!" : "Duets turned off."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const download = async (r) => {
    try {
      const res = await api.get(`/recordings/${r.recording_id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${r.song_title} - SINGVI.wav`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download not available."); }
  };
  const share = (r) => {
    const url = `${window.location.origin}/performance/${r.recording_id}`;
    if (navigator.share) navigator.share({ url }).catch(() => {}); else { navigator.clipboard.writeText(url); toast.success("Link copied!"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head text-4xl font-black tracking-tighter mb-6">My Recordings</h1>
      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="glass rounded-full flex-wrap h-auto">
          {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`rectab-${v}`} className="rounded-full data-[state=active]:bg-neon-pink">{l}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <div className="card-surface rounded-2xl p-12 text-center text-muted-foreground">No recordings here yet. <Link to="/record" className="text-neon-cyan">Record one →</Link></div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const PI = PRIV_ICON[r.privacy] || Lock;
            return (
              <div key={r.recording_id} className="card-surface rounded-2xl p-4 flex items-center gap-4" data-testid={`myrec-${r.recording_id}`}>
                <img src={r.cover_url} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <h4 className="font-head font-bold truncate flex items-center gap-2">{r.song_title}{r.is_duet && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-neon-cyan/90 text-black">Duet</span>}</h4>
                  <p className="text-xs text-muted-foreground truncate">{r.song_artist} · {new Date(r.created_at).toLocaleDateString()}</p>
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground mt-1 capitalize"><PI className="w-3 h-3" /> {r.privacy} · {r.status}</span>
                </div>
                {r.audio_path && <audio controls src={`${API}/recordings/${r.recording_id}/audio`} className="hidden sm:block h-9 w-48" />}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <select value={r.privacy} onChange={(e) => setPrivacy(r.recording_id, e.target.value)} className="bg-input border border-white/10 rounded-lg text-xs px-2 py-1.5" data-testid={`priv-select-${r.recording_id}`}>
                    <option value="private">Private</option><option value="unlisted">Link only</option><option value="public">Public</option>
                  </select>
                  <Button size="icon" variant="ghost" onClick={() => toggleDuet(r)} className={`rounded-full ${r.duet_open ? "text-neon-cyan" : ""}`} title="Open for duets" data-testid={`duet-toggle-${r.recording_id}`}><Users className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => share(r)} className="rounded-full"><Share2 className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => download(r)} className="rounded-full"><Download className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(r.recording_id)} className="rounded-full text-neon-pink" data-testid={`del-${r.recording_id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
